import { memo, useEffect, useMemo, useState } from 'react';
import { Card, Col, Image, Row } from 'react-bootstrap';
import type { LeadCloudFileType } from '@econtractor/types';
import { formatFileSize } from 'utils/helpers/gimHelper';
import { getFileExtension, isPreviewImage } from './helper/filesUtils';
import ItemDropdown from './ItemDropdown';
import type { FilesViewMode } from './model/filesView';
import 'glightbox/dist/css/glightbox.min.css';

interface FilesListProps {
    files: LeadCloudFileType[];
    view: FilesViewMode;
    galleryId: string;
    imageColProps?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
    };
    canDelete?: boolean;
    deletingKey?: string | null;
    onDelete?: (file: LeadCloudFileType) => void;
    noShowPreviewImages?: boolean;
}

const FilesList = memo(
    ({
        files,
        view,
        galleryId,
        imageColProps,
        canDelete,
        deletingKey,
        onDelete,
        noShowPreviewImages,
    }: FilesListProps) => {
        const fileItems = useMemo(() => files.filter((file) => !file.isFolder), [files]);
        const previewFiles = useMemo(
            () => (noShowPreviewImages ? [] : fileItems.filter((file) => isPreviewImage(file.fileName))),
            [fileItems, noShowPreviewImages],
        );
        const colProps = view === 'list' ? { xs: 12 } : imageColProps || { xs: 6, md: 4 };

        useEffect(() => {
            if (!previewFiles.length) return undefined;
            const GLightbox = require('glightbox');
            const lightbox = GLightbox({
                selector: '.glightbox',
            });

            return () => lightbox.destroy();
        }, [previewFiles]);

        if (!fileItems.length) return null;

        return (
            <Row>
                {fileItems.map((file) => (
                    <Col className="p-1" key={file.storageKey} {...colProps}>
                        <OneFile
                            file={file}
                            galleryId={galleryId}
                            canDelete={canDelete}
                            deleting={deletingKey === file.storageKey}
                            onDelete={() => onDelete?.(file)}
                            showPreview={!noShowPreviewImages && isPreviewImage(file.fileName)}
                        />
                    </Col>
                ))}
            </Row>
        );
    },
    (prevProps, nextProps) =>
        prevProps.files === nextProps.files &&
        prevProps.view === nextProps.view &&
        prevProps.deletingKey === nextProps.deletingKey &&
        prevProps.canDelete === nextProps.canDelete &&
        prevProps.onDelete === nextProps.onDelete &&
        prevProps.galleryId === nextProps.galleryId &&
        prevProps.imageColProps === nextProps.imageColProps &&
        prevProps.noShowPreviewImages === nextProps.noShowPreviewImages,
);

interface OneFileProps {
    file: LeadCloudFileType;
    galleryId: string;
    canDelete?: boolean;
    deleting?: boolean;
    onDelete?: () => void;
    showPreview?: boolean;
}

const OneFile = ({ file, galleryId, canDelete, deleting, onDelete, showPreview }: OneFileProps) => {
    const extension = getFileExtension(file.fileName);
    const [src, setSrc] = useState(`/imgs/fileTypes/${extension}.png`);
    const fileSizeLabel = formatFileSize(file.fileSize);

    return (
        <Card className="file-card m-0 h-100">
            <Card.Body className="p-2 file-card-body">
                <div className="file-card-thumb">
                    {showPreview ? (
                        <a
                            className="glightbox d-flex h-100 w-100 align-items-center justify-content-center"
                            href={file.publicUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-gallery={galleryId}
                            title={file.fileName}
                        >
                            <img className="rounded" src={file.publicUrl} alt={file.fileName} />
                        </a>
                    ) : (
                        <a
                            href={file.publicUrl}
                            target="_blank"
                            rel="noreferrer"
                            title={file.fileName}
                            className="file-card-thumb--icon d-flex h-100 w-100 align-items-center justify-content-center"
                        >
                            <Image
                                className="rounded"
                                onError={() => setSrc('/imgs/fileTypes/unknown.png')}
                                src={src}
                                alt={extension || file.fileName}
                            />
                        </a>
                    )}
                </div>
                <div className="file-card-meta min-w-0">
                    <h6 className="d-grid mb-0">
                        <a
                            href={file.publicUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-truncate w-100 no-select text-body text-decoration-none"
                            title={file.fileName}
                        >
                            {file.fileName}
                        </a>
                    </h6>
                    {fileSizeLabel ? (
                        <p className="mb-0 text-muted">
                            <small>{fileSizeLabel}</small>
                        </p>
                    ) : null}
                </div>
                <ItemDropdown
                    canDelete={canDelete}
                    deleting={deleting}
                    copyUrl={file.publicUrl}
                    onDelete={onDelete}
                />
            </Card.Body>
        </Card>
    );
};

export default FilesList;
